import { useEffect, useRef, useState } from "react"
import useLocalStorage from "../use-localstorage"

type UseCollapsibleSidebarOptions = {
  /** Whether the sidebar is collapsed (controlled) */
  collapsed?: boolean
  /** Initial collapsed state (uncontrolled) */
  defaultCollapsed?: boolean
  /**
   * Forces the sidebar collapsed while `true`, overriding the stored preference
   * without writing to it. Entering this state (transition from `false`) always
   * re-collapses; the user can still expand manually, and their toggles persist
   * as normal. Leaving it (`true` → `false`) reverts to the stored preference.
   */
  startCollapsed?: boolean
  /** Called when the collapsed state changes, and once on mount with the initial state. */
  onCollapsedChange?: (collapsed: boolean) => void
}

type UseCollapsibleSidebarResult = {
  collapsed: boolean
  setCollapsed: (next: boolean) => void
}

/**
 * Owns the navigation sidebar's collapsed state across three modes:
 * controlled (`collapsed`), uncontrolled-and-persisted (localStorage), and a
 * non-persisted `startCollapsed` override for routes that should always open
 * collapsed (e.g. editors). See `startCollapsed` for the override semantics.
 */
export function useCollapsibleSidebar({
  collapsed,
  defaultCollapsed = false,
  startCollapsed = false,
  onCollapsedChange
}: UseCollapsibleSidebarOptions): UseCollapsibleSidebarResult {
  const [internalCollapsed, setInternalCollapsed] = useLocalStorage(
    "navigation:collapsed",
    defaultCollapsed
  )
  // Non-persisted override applied while `startCollapsed` is true. `undefined`
  // means "defer to the stored preference"; it is cleared once the user toggles.
  const [override, setOverride] = useState<boolean | undefined>(
    startCollapsed ? true : undefined
  )

  // Re-collapse whenever we enter a force-collapsed context; revert to the
  // stored preference once we leave it.
  const wasForced = useRef(startCollapsed)
  useEffect(() => {
    if (startCollapsed && !wasForced.current) setOverride(true)
    if (!startCollapsed) setOverride(undefined)
    wasForced.current = startCollapsed
  }, [startCollapsed])

  const isCollapsed =
    collapsed !== undefined ? collapsed : (override ?? internalCollapsed)

  useEffect(() => {
    onCollapsedChange?.(isCollapsed)
    // Fires once on mount so consumers can sync to the initial collapsed state
    // (which may be restored from localStorage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setCollapsed = (next: boolean) => {
    if (collapsed === undefined) {
      // The user took control: drop the override and persist as normal so the
      // preference carries to other pages.
      setOverride(undefined)
      setInternalCollapsed(next)
    }
    onCollapsedChange?.(next)
  }

  return { collapsed: isCollapsed, setCollapsed }
}
